import axios from "axios";

import { getFreshIdToken } from "../utils/authToken";
import { readEventStream } from "./studioEvents";

// Calls to the Codenec Studio service (a separate Cloud Run service from the
// main backend). Unlike the backend, it takes the Firebase ID token as an
// Authorization header, because the message route streams and the file routes
// are GETs. Every call gets a fresh token (see utils/authToken.js).
//
// Errors are thrown as StudioError with the service's own code, a message
// already written for the client and the service's `details` (for example
// { resetsAt } with out_of_messages), so pages can show err.message as it is,
// branch on err.code / err.status, and build their own notice from details.
//
// The Studio plan routes live on the MAIN backend (planCall below), so a
// client can see, change and cancel a plan without Studio access.

export const STUDIO_API_URL = (process.env.REACT_APP_STUDIO_API_URL || "").replace(/\/+$/, "");
export const isStudioConfigured = Boolean(STUDIO_API_URL);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export class StudioError extends Error {
  constructor(status, code, message, details = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function headers(user, extra = {}) {
  const token = await getFreshIdToken(user?.accessToken);
  return { Authorization: `Bearer ${token}`, ...extra };
}

async function failure(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    // not JSON
  }
  const details = body?.error?.details;
  return new StudioError(
    res.status,
    body?.error?.code ?? "error",
    body?.error?.message ?? "The Studio could not finish that. Try again in a moment.",
    details && typeof details === "object" ? details : null
  );
}

async function call(user, path, { method = "GET", body } = {}) {
  let res;
  try {
    res = await fetch(`${STUDIO_API_URL}${path}`, {
      method,
      headers: await headers(user, body ? { "Content-Type": "application/json" } : {}),
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new StudioError(0, "offline", "Could not reach the Studio. Check your connection and try again.");
  }
  if (!res.ok) throw await failure(res);
  return res.json();
}

const p = (id) => `/studio/projects/${encodeURIComponent(id)}`;

export const studioApi = {
  me: (user) => call(user, "/studio/me"),
  listProjects: (user) => call(user, "/studio/projects"),
  createProject: (user, name) => call(user, "/studio/projects", { method: "POST", body: { name } }),
  getProject: (user, id) => call(user, p(id)),
  updateProject: (user, id, fields) => call(user, p(id), { method: "PATCH", body: fields }),
  getFile: (user, id, path, version) =>
    call(user, `${p(id)}/files?path=${encodeURIComponent(path)}${version ? `&version=${version}` : ""}`),
  listVersions: (user, id) => call(user, `${p(id)}/versions`),
  restore: (user, id, version) => call(user, `${p(id)}/restore`, { method: "POST", body: { version } }),
  messages: (user, id) => call(user, `${p(id)}/messages`),
  stop: (user, id) => call(user, `${p(id)}/stop`, { method: "POST" }),
  publish: (user, id) => call(user, `${p(id)}/publish`, { method: "POST" }),
  /** Takes the live site offline and frees its live slot. */
  unpublish: (user, id) => call(user, `${p(id)}/unpublish`, { method: "POST" }),
  /** Starts setting up the app's Google project (paid plans). Answers { status }. */
  setup: (user, id) => call(user, `${p(id)}/setup`, { method: "POST" }),

  /** The quick-preview address for a path the service handed out. */
  previewUrl: (path) => `${STUDIO_API_URL}${path}`,

  /** Downloads a version as a zip file through the browser. */
  async download(user, id, version) {
    const res = await fetch(`${STUDIO_API_URL}${p(id)}/download${version ? `?version=${version}` : ""}`, {
      headers: await headers(user),
    });
    if (!res.ok) throw await failure(res);
    const disposition = res.headers.get("content-disposition") || "";
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] || "project.zip";
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /**
   * Sends a message and streams the AI's turn. `kind` is "message" (the
   * default), "continue" (picks up the last turn that hit its work limit) or
   * "fix" ("Fix what broke"); for the last two the server writes the prompt.
   * Refusals (out of messages, paused, busy) throw before any event; after
   * that, every outcome arrives as a "done" event.
   */
  async sendMessage(user, id, { message, model, kind } = {}, onEvent) {
    const body = { message, model };
    if (kind && kind !== "message") body.kind = kind;
    let res;
    try {
      res = await fetch(`${STUDIO_API_URL}${p(id)}/messages`, {
        method: "POST",
        headers: await headers(user, { "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      });
    } catch {
      throw new StudioError(0, "offline", "Could not reach the Studio. Check your connection and try again.");
    }
    if (!res.ok) throw await failure(res);
    await readEventStream(res, onEvent);
  },

  /**
   * The app's hosting plan as the main backend sees it (prices come from Stripe):
   * { price: {amount, currency, interval, intervalCount}, termsVersion, status,
   *   renewsAt, cancelAtPeriodEnd }.
   */
  async hostingPlan(user, projectId) {
    return hostingCall(user, "hosting-plan", { projectId }, "Could not load the hosting plan. Try again in a moment.");
  },

  // New hosting plans are no longer sold (Studio plans include hosting); the
  // calls below only manage the legacy ones that still bill.

  /**
   * Every app of the signed-in user that still has a hosting plan billing:
   * [{ projectId, name, status }]. Works without Studio access, for Account
   * settings, so a client can always find and cancel what charges them.
   */
  async hostingPlans(user) {
    const data = await hostingCall(user, "hosting-plans", {}, "Could not load your hosting plans. Try again in a moment.");
    return Array.isArray(data?.plans) ? data.plans : [];
  },

  /** Cancels online. Resolves to the updated plan (see hostingPlan). */
  async hostingCancel(user, projectId) {
    return hostingCall(user, "hosting-cancel", { projectId }, "Could not cancel the hosting plan. Try again in a moment.");
  },

  /** Undoes a pending cancellation. Resolves to the updated plan. */
  async hostingResume(user, projectId) {
    return hostingCall(user, "hosting-resume", { projectId }, "Could not update the hosting plan. Try again in a moment.");
  },

  // ---- Studio plans (main backend; docs/studio-plans-spec.md section 3.7) ----

  /**
   * The public offer: plans, prices per country, which countries are on sale,
   * the seller block. No sign-in needed.
   */
  async getPlans() {
    try {
      const response = await axios.get(`${API_BASE_URL}/codenec/studio/plans`);
      return response.data;
    } catch (error) {
      throw backendError(error, "Could not load the plans. Try again in a moment.");
    }
  },

  /** The signed-in user's plan: { termsVersion, onSale, plan, billing }. */
  plan: (user) => planCall(user, "plan", {}, "Could not load your plan. Try again in a moment."),

  /**
   * Starts a plan checkout. `body` is what the page showed and the client
   * agreed to: { tier, country, acceptedTerms: true, termsVersion, termsText,
   * price: { amount, currency, tax }, locale, eligibility: { adult: true,
   * country } }. Resolves to the Stripe Checkout URL.
   */
  async planCheckout(user, body) {
    const data = await planCall(user, "plan-checkout", body, "Could not start the checkout. Try again in a moment.");
    return data.url;
  },

  /** What an upgrade costs now, or what a downgrade changes. */
  planChangePreview: (user, toTier) =>
    planCall(user, "plan-change-preview", { toTier }, "Could not load the change. Try again in a moment."),

  /**
   * Upgrades, or schedules a downgrade: { toTier, keepProjectIds?,
   * acceptedTerms: true, termsVersion, termsText, prorationDate?,
   * previewAmount?, locale }. Resolves to { plan, billing }.
   */
  planChange: (user, body) => planCall(user, "plan-change", body, "Could not change your plan. Try again in a moment."),

  /** Undoes a scheduled downgrade. Resolves to { plan, billing }. */
  planDowngradeCancel: (user) =>
    planCall(user, "plan-downgrade-cancel", {}, "Could not undo the change. Try again in a moment."),

  /** Cancels online; the plan runs to the end of the paid month. Resolves to { plan, billing }. */
  planCancel: (user) => planCall(user, "plan-cancel", {}, "Could not cancel your plan. Try again in a moment."),

  /** Undoes a cancellation the client made. Resolves to { plan, billing }. */
  planResume: (user) => planCall(user, "plan-resume", {}, "Could not update your plan. Try again in a moment."),

  /** The Stripe billing portal for the Studio customer. Resolves to its URL. */
  async planPortal(user) {
    const data = await planCall(user, "plan-portal", {}, "Could not open billing. Try again in a moment.");
    return data.url;
  },
};

// The hosting and plan routes live on the MAIN backend, not the Studio
// service. They answer { error: <code>, code, message } on failure.
async function backendPost(user, route, body, fallbackMessage) {
  const token = await getFreshIdToken(user?.accessToken);
  try {
    const response = await axios.post(`${API_BASE_URL}/codenec/studio/${route}`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    throw backendError(error, fallbackMessage);
  }
}
const hostingCall = backendPost;
const planCall = backendPost;

// A StudioError from a backend (axios) failure. `details` keeps the rest of
// the answer, for example hostedInvoiceUrl with payment_action_required.
export function backendError(error, fallbackMessage) {
  const data = error?.response?.data;
  let details = null;
  if (data && typeof data === "object") {
    const rest = { ...data };
    delete rest.error;
    delete rest.code;
    delete rest.message;
    if (Object.keys(rest).length) details = rest;
  }
  return new StudioError(error?.response?.status ?? 0, backendErrorCode(error), data?.message || fallbackMessage, details);
}

// The backend answers { error: <code>, message }, but the sign-in and
// rate-limit middleware answer { error: <sentence>, code }. Map both onto codes
// that have studio.errors.* text in English and Spanish.
export function backendErrorCode(error) {
  const status = error?.response?.status ?? 0;
  const data = error?.response?.data || {};
  const raw = typeof data.code === "string" ? data.code : typeof data.error === "string" ? data.error : "";
  if (status === 0) return "offline";
  if (raw === "auth_required" || raw === "auth_invalid" || raw === "account_required") return "signed_out";
  if (raw === "rate_limited" || raw === "daily_limit" || raw === "limit_unavailable") return "slow_down";
  if (/^(hosting|plan)_\w+_failed$/.test(raw)) return "try_again";
  return /^[a-z_]+$/.test(raw) ? raw : "unknown";
}
